import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

export default function CookieConsent() {
  const [showBanner, setShowBanner] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true,
    functional: true,
    analytics: false,
  });

  useEffect(() => {
    const consent = localStorage.getItem('cookie-consent');
    if (!consent) {
      setShowBanner(true);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      functional: true,
      analytics: true,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  const acceptSelected = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      ...preferences,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
    setShowCustomize(false);
  };

  const rejectAll = () => {
    localStorage.setItem('cookie-consent', JSON.stringify({
      necessary: true,
      functional: false,
      analytics: false,
      timestamp: new Date().toISOString()
    }));
    setShowBanner(false);
  };

  if (!showBanner) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 sm:p-6 animate-slide-up">
      <div className="max-w-6xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200">
        {!showCustomize ? (
          <div className="p-6 sm:p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <i className="ri-cookie-line text-2xl text-orange-600"></i>
              </div>
              <div className="flex-1">
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  Nous utilisons des cookies
                </h3>
                <p className="text-gray-600 leading-relaxed">
                  Nous utilisons des cookies pour améliorer votre expérience sur notre site, analyser le trafic et personnaliser le contenu. En cliquant sur "Tout accepter", vous consentez à l'utilisation de tous les cookies.
                </p>
                <Link 
                  to="/politique-de-cookies" 
                  className="text-orange-600 hover:text-orange-700 font-medium text-sm mt-2 inline-block"
                >
                  En savoir plus sur notre politique de cookies
                </Link>
              </div>
            </div>
            
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptAll}
                className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Tout accepter
              </button>
              <button
                onClick={() => setShowCustomize(true)}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                Personnaliser
              </button>
              <button
                onClick={rejectAll}
                className="flex-1 px-6 py-3 bg-white text-gray-700 border-2 border-gray-300 rounded-lg font-semibold hover:bg-gray-50 transition-all whitespace-nowrap"
              >
                Tout refuser
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 sm:p-8">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">
                Personnaliser les cookies
              </h3>
              <button
                onClick={() => setShowCustomize(false)}
                className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition-colors"
              >
                <i className="ri-close-line text-xl text-gray-600"></i>
              </button>
            </div>

            <div className="space-y-4 mb-6">
              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-1">
                    Cookies nécessaires
                  </div>
                  <p className="text-sm text-gray-600">
                    Ces cookies sont essentiels au fonctionnement du site et ne peuvent pas être désactivés.
                  </p>
                </div>
                <div className="ml-4">
                  <div className="w-12 h-6 bg-gray-400 rounded-full flex items-center px-1 cursor-not-allowed">
                    <div className="w-4 h-4 bg-white rounded-full transform translate-x-6"></div>
                  </div>
                </div>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-1">
                    Cookies fonctionnels
                  </div>
                  <p className="text-sm text-gray-600">
                    Ces cookies permettent d'améliorer les fonctionnalités et la personnalisation du site.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, functional: !prev.functional }))}
                  className="ml-4"
                >
                  <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.functional ? 'bg-orange-500' : 'bg-gray-300'
                  }`}>
                    <div className={`w-4 h-4 bg-white rounded-full transform transition-transform ${
                      preferences.functional ? 'translate-x-6' : 'translate-x-0'
                    }`}></div>
                  </div>
                </button>
              </div>

              <div className="flex items-start justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex-1">
                  <div className="font-semibold text-gray-900 mb-1">
                    Cookies analytiques
                  </div>
                  <p className="text-sm text-gray-600">
                    Ces cookies nous aident à comprendre comment les visiteurs utilisent notre site.
                  </p>
                </div>
                <button
                  onClick={() => setPreferences(prev => ({ ...prev, analytics: !prev.analytics }))}
                  className="ml-4"
                >
                  <div className={`w-12 h-6 rounded-full flex items-center px-1 transition-colors ${
                    preferences.analytics ? 'bg-orange-500' : 'bg-gray-300'
                  }`}>
                    <div className={`w-4 h-4 bg-white rounded-full transform transition-transform ${
                      preferences.analytics ? 'translate-x-6' : 'translate-x-0'
                    }`}></div>
                  </div>
                </button>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={acceptSelected}
                className="flex-1 px-6 py-3 bg-orange-500 text-white rounded-lg font-semibold hover:bg-orange-600 transition-all shadow-lg hover:shadow-xl whitespace-nowrap"
              >
                Enregistrer mes préférences
              </button>
              <button
                onClick={acceptAll}
                className="flex-1 px-6 py-3 bg-gray-100 text-gray-900 rounded-lg font-semibold hover:bg-gray-200 transition-all whitespace-nowrap"
              >
                Tout accepter
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
